<template>
  <div
    v-show="isActive"
    :id="boxId"
    :class="b()"
  />
</template>

<script>
import theme from '@zk/theme/lib/variables.scss';
import { loadMicroApp } from 'qiankun';

export const ACTIVE_MICRO_APP_MOUNTED_EVENT_NAME = 'activeMicroAppMounted';

export default {
  name: 'MicroApp',
  inject: ['appLayout'],
  props: {
    app: {
      type: Object,
      required: true
    }
  },
  bemNS: theme.bemNS,
  data() {
    return {
      loadTimer: null
    };
  },
  computed: {
    boxId() {
      return `micro-app_${this.app.name}`;
    },
    activeRule() {
      return this.app.activeRule;
    },
    currentPath() {
      return this.$route.fullPath;
    },
    isActive() {
      const { activeRule, currentPath } = this;
      const rules = Array.isArray(activeRule) ? [ ...activeRule ] : [activeRule];
      return rules.some(rule => {
        if (typeof rule === 'function') {
          return rule(currentPath);
        }
        return currentPath.startsWith(`${rule}`);
      });
    },
    isKeepalive() {
      return this.app.keepalive;
    }
  },
  watch: {
    isActive: {
      handler() {
        this.onActiveChange();
      }
    }
  },
  created () {
    // 需要等spa start后再加载应用，才会有shadow节点
    this.$once('started', () => {
      this.init();
    });
    // 把当前实例加入到layout中
    this.appLayout.apps.set(this.app.name, this);
  },
  methods: {
    init() {
      // 预挂载
      if (this.app.preload) {
        this.load();
      }
      // 如果路由直接进入当前应用则会在这里挂载
      this.onActiveChange();
    },
    /**
     * 加载微应用
     * @returns {Promise<void>}
     */
    async load() {
      if (!this.appInstance) {
        if (this.loadTimer && this.isActive) {
          // 对于尚未生成appInstance但是切换为active的app来说，直接load
          return await this.loadCurrent();
        }
        if (!this.isActive) {
          // 为了防止一些其他因素导致无法正常触发以下时间，做一个兜底策略
          this.eventBus.$on(ACTIVE_MICRO_APP_MOUNTED_EVENT_NAME, this.loadCurrent);
          this.loadTimer = setTimeout(this.loadCurrent, 2000);
          return;
        }
        await this.loadCurrent();
        this.eventBus.$emit(ACTIVE_MICRO_APP_MOUNTED_EVENT_NAME);
      }
    },
    /**
     * 加载当前的微应用
     * @returns {Promise<void>}
     */
    async loadCurrent() {
      if (this.loadTimer) {
        // 取消当前注册的回调
        clearTimeout(this.loadTimer);
        this.loadTimer = null;
        this.eventBus.$off(ACTIVE_MICRO_APP_MOUNTED_EVENT_NAME, this.loadCurrent);
      }
      if (this.appInstance) {
        return;
      }
      const { name, entry, preload } = this.app;
      const { toggleAside, toggleVisibleAside, toggleVisibleHeader } = this.appLayout;
      const mainInstance = document.querySelector('#master-container').__vue__;
      this.appInstance = loadMicroApp({
        name,
        entry,
        container: `#${this.boxId}`,
        props: {
          store: this.$store,
          pinia: this.$pinia,
          i18n: this.$i18n,
          appName: name,
          preload,
          active: this.isActive,
          filters: this.filters,
          directives: this.directives,
          provided: mainInstance._provided,
          prototypes: {
            eventBus: this.eventBus,
            microRouter: this.$router,
            $track: this.$track,
            toggleAside,
            toggleVisibleAside,
            toggleVisibleHeader
          }
        }
      });
      await this.appInstance.mountPromise;
    },
    /**
     * 状态变更
     * @returns {Promise<void>}
     */
    async onActiveChange() {
      // 触发全局事件
      this.eventBus.$emit(`${this.isActive ? 'activated' : 'deactivated'}:${this.app.name}`);
      // 如果当前为激活则加载
      if (this.isActive) {
        await this.load();
      }
      // 如果当前为失效并且当前应用已加载并且配置为不缓存则卸载当前应用
      if (!this.isActive && this.appInstance && !this.isKeepalive) {
        await this.appInstance.unmount();
        this.appInstance = null;
      }
      // 通知布局当前状态变更
      this.$emit('active', this.isActive);
    }
  }
};
</script>

<style lang="scss" scoped>
@include b(micro-app) {
  height: 100%;
  width: 100%;
  ::v-deep div[id^=__qiankun_microapp_wrapper] {
    height: 100%;
    width: 100%;
  }
}
</style>
